import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { euro, euroInt, eurOnly, setDisplayCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Printer, ArrowLeft, Sparkles } from "lucide-react";
import { stripParen, extractParen, priceTtcOf } from "@/lib/format";
import { ModelInfo } from "@/components/ModelInfo";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const frDate = (d) => (d || "").slice(0, 10).split("-").reverse().join("/");

export default function PriceList() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ranges, setRanges] = useState([]);
  const [rangeKey, setRangeKey] = useState("van");
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState(() => localStorage.getItem("fautras_country") || "FR");
  const [cat, setCat] = useState({ models: [], options: [] });
  const [loading, setLoading] = useState(true);
  const [promos, setPromos] = useState([]);
  const [checkedPromos, setCheckedPromos] = useState(() => new Set());
  const activePromos = useMemo(() => promos.filter((p) => checkedPromos.has(p.id)), [promos, checkedPromos]);
  const applyPromo = activePromos.length > 0;
  const togglePromo = (id, on) => setCheckedPromos((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const [islandaisNames, setIslandaisNames] = useState(() => new Set());
  const [placesByName, setPlacesByName] = useState({});
  const rootRef = useRef(null);
  const [hiddenCats, setHiddenCats] = useState(() => new Set());
  const [sizeFilter, setSizeFilter] = useState({ std: true, isl: false });
  const [showModels, setShowModels] = useState(true);
  const [showOptions, setShowOptions] = useState(true);
  const [showPublicRaw, setShowPublic] = useState(true);
  const noPublic = rangeKey === "prosport" && countryCode !== "FR";
  const showPublic = showPublicRaw && !noPublic;
  const [showReseller, setShowReseller] = useState(false);
  useEffect(() => {
    if (noPublic) setShowReseller(true);
  }, [noPublic]);

  useEffect(() => {
    setCheckedPromos(new Set());
    setHiddenCats(new Set());
    setSizeFilter({ std: true, isl: false });
    setShowModels(true);
    setShowOptions(true);
    if (!rangeKey || !countryCode) { setPromos([]); return; }
    api.get(`/promotions/active`, { params: { country: countryCode, range_key: rangeKey, all_countries: isAdmin } })
      .then(({ data }) => setPromos((data || []).filter((p) => !p.hide_in_prices))).catch(() => setPromos([]));
  }, [rangeKey, countryCode, isAdmin]);

  useEffect(() => {
    if (!rangeKey) { setIslandaisNames(new Set()); return; }
    api.get(`/menu/${rangeKey}`).then(({ data }) => {
      const names = new Set();
      const pl = {};
      for (const g of (data.groups || [])) {
        const n = (g.label || "").match(/^([\d,.]+)\s*place/i)?.[1];
        if (n) for (const e of (g.entries || [])) {
          for (const v of (e.versions || [])) { if (v.model?.id) pl[v.model.id] = n; }
          if (e.model?.id) pl[e.model.id] = n;
        }
      }
      setPlacesByName(pl);
      for (const g of (data.groups || [])) for (const e of (g.entries || [])) {
        if (!((e.size || "").toLowerCase().includes("island"))) continue;
        if (e.versions) for (const v of e.versions) { if (v.match) names.add(v.match); }
        if (e.match) names.add(e.match);
        if (e.label) names.add(e.label);
      }
      setIslandaisNames(names);
    }).catch(() => setIslandaisNames(new Set()));
  }, [rangeKey]);

  const isModelIsl = (m) => islandaisNames.has(m.name) || (m.name || "").toLowerCase().includes("icelandic");
  const anyIsl = useMemo(() => rangeKey === "van" && cat.models.some((m) => isModelIsl(m)), [cat, rangeKey, islandaisNames]);

  const promoMaps = useMemo(() => {
    const model = {}, option = {}, noOpt = new Set();
    if (!applyPromo) return { model, option, noOpt };
    for (const p of activePromos) {
      for (const [id, r] of Object.entries(p.model_rules || {})) {
        if (!model[id] || (r.pub || 0) > model[id].pub) model[id] = { pub: r.pub || 0, res: r.res || 0 };
      }
      for (const [id, r] of Object.entries(p.option_rules || {})) {
        if (!option[id] || (r.pub || 0) > option[id].pub) option[id] = { pub: r.pub || 0, res: r.res || 0 };
      }
      for (const id of (p.no_option_promo_models || [])) noOpt.add(id);
    }
    return { model, option, noOpt };
  }, [activePromos, applyPromo]);

  const allModelCats = useMemo(() => {
    const seen = new Set(); const out = [];
    for (const m of cat.models) {
      const c = ((m.category || "").trim()) || "Divers";
      if (!seen.has(c)) { seen.add(c); out.push(c); }
    }
    return out;
  }, [cat]);
  const toggleCat = (c) => setHiddenCats((prev) => { const n = new Set(prev); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const [hiddenOptCats, setHiddenOptCats] = useState(() => new Set());
  const allOptionCats = useMemo(() => [...new Set(cat.options.map((o) => ((o.category || "").trim()) || "Divers"))], [cat]);
  const toggleOptCat = (c) => setHiddenOptCats((prev) => { const n = new Set(prev); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const catTransport = (c) => { const u = (c || "").toUpperCase(); if (u.includes("DIAGONAL")) return "diagonal"; if (u.includes("DROIT")) return "droit"; return null; };
  const visibleTransports = useMemo(() => {
    const s = new Set();
    for (const c of allModelCats) { if (hiddenCats.has(c)) continue; const t = catTransport(c); if (t) s.add(t); }
    return s;
  }, [allModelCats, hiddenCats]);
  const optionPass = (o) => {
    if (rangeKey !== "van") return true;
    const vt = o.van_transport;
    if (!vt || !vt.length) return true;
    return vt.some((t) => visibleTransports.has(t));
  };
  const sizePass = (m) => rangeKey !== "van" ? true : (isModelIsl(m) ? sizeFilter.isl : sizeFilter.std);
  const visibleModels = useMemo(() => showModels ? cat.models.filter((m) => !hiddenCats.has(((m.category || "").trim()) || "Divers") && sizePass(m)) : [], [cat, hiddenCats, sizeFilter, islandaisNames, rangeKey, showModels]);
  const visibleOptions = useMemo(() => showOptions ? cat.options.filter((o) => optionPass(o) && !hiddenOptCats.has(((o.category || "").trim()) || "Divers")) : [], [cat, showOptions, visibleTransports, rangeKey, hiddenOptCats]);

  useEffect(() => { api.get("/ranges").then(({ data }) => setRanges(data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/catalog/${rangeKey}`, { params: rangeKey === "van" ? { country: countryCode } : {} }).then(({ data }) => {
      setCat(data); setPricing(data.pricing || null);
      if (data.pricing && data.pricing.country !== countryCode) setCountryCode(data.pricing.country);
    }).finally(() => setLoading(false));
  }, [rangeKey, countryCode]);
  const [pricing, setPricing] = useState(null);
  const cur = pricing?.currency || "EUR";
  setDisplayCurrency(cur, pricing?.rate || 1);
  useEffect(() => () => setDisplayCurrency(), []);
  const eq = (v) => (cur !== "EUR" ? <div className="text-[10px] text-muted-foreground font-normal">{eurOnly(v)}</div> : null);

  const vat = useMemo(() => { const c = countries.find((x) => x.code === countryCode); return c ? c.vat_rate : 0.2; }, [countries, countryCode]);
  const countryName = useMemo(() => countries.find((c) => c.code === countryCode)?.name || countryCode, [countries, countryCode]);
  const rangeName = useMemo(() => ranges.find((r) => r.key === rangeKey)?.name || rangeKey, [ranges, rangeKey]);

  const row = (item, i, kind, lead = null) => {
    const rule = (kind === "model" ? promoMaps.model : promoMaps.option)[item.id];
    const promoted = !!rule && ((rule.pub || 0) > 0 || (rule.res || 0) > 0);
    const resHt = (item.reseller_ht || 0) * (1 - (rule?.res || 0) / 100);
    const ttc = priceTtcOf(item, vat) * (1 - (rule?.pub || 0) / 100);
    const pubHt = ttc / (1 + vat);
    const tva = ttc - pubHt, marge = pubHt - resHt;
    const pc = promoted ? " text-[#0050b3] font-bold" : "";
    return (
      <tr key={item.id} className={i % 2 ? "bg-[#efe6d4]" : "bg-white"}>
        {lead}
        <td className="px-3 py-1.5 border-b border-r border-[#e8e0d2]">
          {kind === "model" ? t(stripParen(item.name)) : stripParen(t(item.name))}
          <ModelInfo name={kind === "model" ? stripParen(item.name) : item.name} details={item.details || extractParen(item.name)} className="ml-1 align-middle no-print" />
          {promoted && <span className="no-print ml-2 inline-block rounded bg-[#9E7B41] text-white text-[10px] font-semibold px-1.5 py-0.5 align-middle" data-testid={`prices-promo-badge-${item.id}`}>PROMO</span>}
          {kind === "model" && applyPromo && promoMaps.noOpt.has(item.id) && <span className="ml-2 inline-block rounded border border-[#9E7B41] text-[#9E7B41] text-[10px] font-semibold px-1.5 py-0.5 align-middle" data-testid={`prices-nooptpromo-${item.id}`}>{t("Promo options non applicable")}</span>}
        </td>
        {showReseller && <td className={`px-3 py-1.5 border-b border-r border-[#e8e0d2] text-right font-mono whitespace-nowrap${pc}`} data-testid={`prices-res-${item.id}`}>{euroInt(resHt)}{eq(resHt)}</td>}
        {showPublic && <td className={`px-3 py-1.5 border-b border-r border-[#e8e0d2] text-right font-mono whitespace-nowrap${pc}`}>{euro(pubHt)}{eq(pubHt)}</td>}
        {showPublic && <td className={`px-3 py-1.5 border-b border-r border-[#e8e0d2] text-right font-mono whitespace-nowrap${pc}`}>{euro(tva)}</td>}
        {showPublic && <td className={`px-3 py-1.5 border-b border-r border-[#e8e0d2] text-right font-mono whitespace-nowrap ${promoted ? "font-bold text-[#0050b3]" : "font-semibold text-[#9E7B41]"}`} data-testid={`prices-ttc-${item.id}`}>{euroInt(ttc)}{eq(ttc)}</td>}
        {showReseller && !noPublic && <td className={`px-3 py-1.5 border-b border-[#e8e0d2] text-right font-mono whitespace-nowrap${pc}`}>{euroInt(marge)}{eq(marge)}</td>}
      </tr>
    );
  };

  const showPlaces = rangeKey === "van" && Object.keys(placesByName).length > 0;
  const colCount = 1 + (showReseller ? (noPublic ? 1 : 2) : 0) + (showPublic ? 3 : 0);
  const sectionHead = (title, n = colCount + 1) => (
    <tr className="bg-[#2b2b2b] text-white"><td colSpan={n} className="px-3 py-2 font-display font-bold uppercase tracking-wide text-sm">{title}</td></tr>
  );
  const subHead = (title, n = colCount) => (
    <tr className="bg-[#e8dcc3] text-[#2b2b2b]"><td colSpan={n} className="px-3 py-1.5 font-semibold uppercase tracking-wide text-xs">{title}</td></tr>
  );
  const groupByCat = (items) => {
    const map = new Map();
    for (const it of items) {
      const c = ((it.category || "").trim()) || t("Divers");
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(it);
    }
    return [...map.entries()];
  };
  const placesCell = (n, span, c) => (
    <td key={`pl-${c}-${n}`} rowSpan={span} className="w-7 px-0.5 py-2 bg-[#f3ecdf] border-b border-r border-[#d8c9a8] align-middle" data-testid={`prices-places-cell-${c}-${n || "none"}`}>
      {n && (span >= 4
        ? <div className="[writing-mode:vertical-rl] rotate-180 mx-auto font-bold uppercase tracking-wide text-[10px] text-[#9E7B41] whitespace-nowrap">{n} {t(n === "1,5" || n === "1" ? "place" : "places")}</div>
        : <div className="text-center font-bold text-[9px] leading-tight text-[#9E7B41] whitespace-nowrap">{n} {t("pl.")}</div>)}
    </td>
  );
  // One block per category: category name written vertically on the left, blank space after the block
  const catBlock = (c, items, kind) => {
    const rows = [];
    const withPlaces = kind === "model" && showPlaces;
    const groups = withPlaces ? groupByPlaces(items) : [["", items]];
    for (const [n, list] of groups) list.forEach((it, i) => rows.push({ item: it, i, pl: withPlaces && i === 0 ? placesCell(n, list.length, c) : null }));
    const lead = (
      <td key={`cat-${c}`} rowSpan={rows.length} className="w-8 px-1 py-2 bg-[#e8dcc3] border-b border-r border-[#d8c9a8] align-middle" data-testid={`prices-cat-cell-${kind}-${c}`}>
        <div className={`[writing-mode:vertical-rl] rotate-180 mx-auto font-display font-bold uppercase tracking-wide text-[#2b2b2b] whitespace-nowrap ${rows.length < 8 ? "text-[9px] leading-tight" : "text-[11px]"}`}>
          {rows.length < 8 ? t(c).split(" ").map((w, j) => <span key={j}>{j > 0 && <br />}{w}</span>) : t(c)}
        </div>
      </td>
    );
    return (
      <tbody key={kind + "-" + c} className="prices-cat-block">
        {rows.map((r, k) => row(r.item, k, kind, (k === 0 || r.pl) ? [k === 0 ? lead : null, r.pl] : null))}
        <tr aria-hidden="true"><td colSpan={colCount + (withPlaces ? 2 : 1)} className="h-4 p-0 bg-white border-0" /></tr>
      </tbody>
    );
  };
  const groupByPlaces = (items) => {
    const map = new Map();
    for (const m of items) { const n = placesByName[m.id] || ""; if (!map.has(n)) map.set(n, []); map.get(n).push(m); }
    return [...map.entries()];
  };
  const colHead = (places = false) => (
    <tr className="bg-[#9E7B41] text-white text-xs uppercase tracking-wide">
      <th className="w-8 border-r border-[#b8935a]" aria-label={t("Catégorie")} />
      {places && <th className="w-7 border-r border-[#b8935a]" aria-label={t("Places")} />}
      <th className="px-3 py-2 text-center border-r border-[#b8935a]">{t("Désignation")}</th>
      {showReseller && <th className="w-[1%] px-2 py-2 text-center border-r border-[#b8935a]">{t("Prix revendeur HT")}</th>}
      {showPublic && <th className="w-[1%] px-2 py-2 text-center border-r border-[#b8935a]">{t("Prix public HT")}</th>}
      {showPublic && <th className="w-[1%] px-2 py-2 text-center border-r border-[#b8935a]">{t("TVA")}</th>}
      {showPublic && <th className="w-[1%] px-2 py-2 text-center border-r border-[#b8935a]">{t("Prix public TTC")}</th>}
      {showReseller && !noPublic && <th className="w-[1%] px-2 py-2 text-center">{t("Marge revendeur HT")}</th>}
    </tr>
  );
  const pubIncl = rangeKey === "van" ? ["FR", "CH"].includes(pricing?.country || countryCode) : countryCode === "FR";
  const resIncl = rangeKey === "van" && (pricing?.country || countryCode) === "FR";
  const incl = t("transport inclus jusqu'au point de vente"), excl = t("hors transport");
  const transportNote = showReseller && showPublic
    ? ` · ${t("Prix revendeur")} : ${resIncl ? incl : excl} · ${t("Prix public")} : ${pubIncl ? incl : excl}`
    : showReseller ? ` · ${t("Prix revendeur")} : ${resIncl ? incl : excl}` : showPublic ? ` · ${t("Prix public")} : ${pubIncl ? incl : excl}` : "";
  const onePage = rangeKey === "prosport";
  const optRows = visibleOptions.length + groupByCat(visibleOptions).length + 2 + (cur !== "EUR" ? visibleOptions.length : 0)
    + (onePage && visibleModels.length ? visibleModels.length + groupByCat(visibleModels).length + 3 : 0);
  const optFont = Math.max(5.5, Math.min(10.5, ((visibleModels.length && !onePage ? 1060 : 930) / optRows - 2.5) / 1.12)).toFixed(1);

  const tableRules = `.prices-table th, .prices-table td { font-size: var(--mod-font, 10.5px) !important; } .prices-table thead th { padding: 3px 5px !important; } .prices-table tbody td { padding: 1px 5px !important; line-height: 1.2 !important; } .prices-options-table th, .prices-options-table td { font-size: var(--opt-font, ${optFont}px) !important; } .prices-options-table tbody td { padding: 0 5px !important; line-height: 1.12 !important; } .prices-table td span.rounded { font-size: 6.5px !important; padding: 0 3px !important; line-height: 1.3 !important; } .prices-table .no-print { display: none !important; }`;
  const measureRules = tableRules.replace(/(^|\}\s*)([^{}]+)\{/g, (m, a, sel) => `${a}${sel.split(",").map((s) => `body.prices-measure ${s.trim()}`).join(", ")} {`);
  // Before printing: largest font (max 14px) that keeps each printed page within one A4 sheet
  useEffect(() => {
    const fitPrint = () => {
      const root = rootRef.current;
      if (!root) return;
      const PAGE = 1030;
      const h = (k) => [...root.querySelectorAll(`[data-fit="${k}"]`)].reduce((s, e) => s + e.offsetHeight + 16, 0);
      const fit = (v, parts) => {
        let f = 14;
        root.style.setProperty(v, `${f}px`);
        while (f > 5.5 && parts.reduce((s, k) => s + h(k), 0) > PAGE) { f -= 0.25; root.style.setProperty(v, `${f}px`); }
      };
      document.body.classList.add("prices-measure");
      if (onePage || !visibleModels.length) fit("--opt-font", ["head", "models", "options", "promo"]);
      else { fit("--mod-font", ["head", "models"]); fit("--opt-font", ["options", "promo"]); }
      document.body.classList.remove("prices-measure");
    };
    window.addEventListener("beforeprint", fitPrint);
    const mq = window.matchMedia("print");
    const onMq = (e) => { if (e.matches) fitPrint(); };
    mq.addEventListener?.("change", onMq);
    const tm = setTimeout(fitPrint, 300);
    return () => { window.removeEventListener("beforeprint", fitPrint); mq.removeEventListener?.("change", onMq); clearTimeout(tm); };
  }, [onePage, visibleModels.length, visibleOptions.length, showReseller, showPublic, cur]);

  return (
    <div ref={rootRef} className="prices-root max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <style>{`body.prices-measure .prices-root { width: 771px !important; max-width: none !important; padding: 0 !important; } ${measureRules} @media print { html, body, #root, #root > div, .prices-root { background: #fff !important; margin: 0 !important; padding: 0 !important; max-width: none !important; width: 100% !important; min-height: 0 !important; } .prices-root .rounded-xl { border-radius: 0 !important; border: 0 !important; overflow: visible !important; } main, #root > div { padding: 0 !important; } header { display: none !important; } .no-print { display: none !important; } body { background:#fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } @page { size: A4 portrait; margin: 3mm; } ${tableRules} tr, td, th { break-inside: avoid; } .prices-cat-block { break-inside: avoid; } .prices-print-break { page-break-before: always; break-before: page; } }`}</style>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/selection" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="prices-back"><ArrowLeft className="w-4 h-4" /> {t("Retour")}</Link>
          <Link to="/configurer" data-testid="prices-back-configurator">
            <Button variant="outline" size="sm" className="gap-1.5"><Sparkles className="w-4 h-4" /> {t("Retour au configurateur")}</Button>
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="w-full sm:w-48" data-testid="prices-range"><SelectValue /></SelectTrigger>
            <SelectContent>{ranges.map((r) => <SelectItem key={r.key} value={r.key}>{t(r.name)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={countryCode} onValueChange={(v) => { setCountryCode(v); localStorage.setItem("fautras_country", v); }}>
            <SelectTrigger className="w-full sm:w-56" data-testid="prices-country"><SelectValue /></SelectTrigger>
            <SelectContent>{countries.filter((c) => !pricing?.allowed || rangeKey !== "van" || pricing.allowed.includes(c.code)).map((c) => <SelectItem key={c.code} value={c.code}>{t(c.name)} · {t("TVA")} {Math.round(c.vat_rate * 1000) / 10}%</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => window.print()} className="gap-2" data-testid="prices-print"><Printer className="w-4 h-4" /> {t("Imprimer")}</Button>
        </div>
      </div>

      {promos.length > 0 && (
        <div className="mb-4 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 no-print" data-testid="prices-promo-row">
          <div className="text-sm mb-1.5">{t("Appliquer la promotion :")} <span className="text-muted-foreground">— {t("les tarifs affichés sont recalculés.")}</span></div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {promos.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox checked={checkedPromos.has(p.id)} onCheckedChange={(v) => togglePromo(p.id, !!v)} data-testid={`prices-promo-checkbox-${p.id}`} />
                {p.name}
                {!(p.country_codes || []).some((c) => c.toUpperCase() === String(countryCode).toUpperCase()) && (
                  <span className="text-[10px] font-mono rounded bg-amber-100 text-amber-800 px-1.5 py-0.5" data-testid={`prices-promo-country-${p.id}`}>{(p.country_codes || []).join(" · ")}</span>)}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 space-y-3 no-print" data-testid="prices-filters">
        {rangeKey === "van" && anyIsl && (
          <div className="flex flex-wrap items-end gap-16">
            <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="prices-size-std">
              <Checkbox checked={sizeFilter.std} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, std: !!v }))} /><img src="/assets/cheval-standard.png" alt="" className="max-h-9 w-auto" />{t("Chevaux standard")}
            </label>
            <label className="flex items-end gap-2 text-sm cursor-pointer" data-testid="prices-size-isl">
              <Checkbox checked={sizeFilter.isl} onCheckedChange={(v) => setSizeFilter((s) => ({ ...s, isl: !!v }))} /><img src="/assets/cheval-islandais.png" alt="" className="max-h-6 w-auto" />{t("Chevaux islandais")}
            </label>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("Afficher :")}</span>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="prices-show-models">
            <Checkbox checked={showModels} onCheckedChange={(v) => setShowModels(!!v)} />{t("Modèles")}
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="prices-show-options">
            <Checkbox checked={showOptions} onCheckedChange={(v) => setShowOptions(!!v)} />{t("Options")}
          </label>
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground ml-2">{t("Tarifs :")}</span>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="prices-show-public">
            <Checkbox checked={showPublic} disabled={noPublic} onCheckedChange={(v) => setShowPublic(!!v)} />{t("Public")}
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="prices-show-reseller">
            <Checkbox checked={showReseller} onCheckedChange={(v) => setShowReseller(!!v)} />{t("Revendeur")}
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mr-1">{t("Catégories :")}</span>
          {allModelCats.map((c) => {
            const on = !hiddenCats.has(c);
            return (
              <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`prices-cat-${c}`}
                className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${on ? "bg-[#9E7B41] text-white border-[#9E7B41]" : "bg-transparent text-muted-foreground border-border"}`}>
                {t(c)}
              </button>
            );
          })}
        </div>
        {showOptions && allOptionCats.length > 1 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="prices-opt-cats">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mr-1">{t("Catégories d'options :")}</span>
            {allOptionCats.map((c) => {
              const on = !hiddenOptCats.has(c);
              return (
                <button key={c} type="button" onClick={() => toggleOptCat(c)} data-testid={`prices-optcat-${c}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1 border transition-colors ${on ? "bg-[#2b2b2b] text-white border-[#2b2b2b]" : "bg-transparent text-muted-foreground border-border"}`}>
                  {t(c)}
                </button>
              );
            })}
            <button type="button" className="text-xs underline text-muted-foreground ml-1" onClick={() => setHiddenOptCats(hiddenOptCats.size ? new Set() : new Set(allOptionCats))} data-testid="prices-optcat-toggle-all">
              {hiddenOptCats.size ? t("Tout cocher") : t("Tout décocher")}
            </button>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center gap-3" data-fit="head">
        <img src="/assets/fautras-logo.png" alt="Fautras" className="h-10 w-auto" />
        <div>
          <h1 className="font-display text-2xl font-extrabold" data-testid="prices-title">{showReseller && showPublic ? t("Tarifs revendeur et public") : showReseller ? t("Tarifs revendeur") : t("Tarifs publics")}</h1>
          <p className="text-sm text-muted-foreground" data-testid="prices-subtitle">{t(rangeName)} · {t(countryName)} · {t("TVA")} {Math.round(vat * 1000) / 10}%{cur !== "EUR" ? ` · ${t("Prix en")} ${cur} (1 € = ${pricing.rate} ${cur}${pricing.rate_date ? `, ${t("cours BCE du")} ${pricing.rate_date}` : ""})` : ""}{pricing && !pricing.available ? ` · ${t("Pas encore de tarif pour ce pays (prix France)")}` : ""}{transportNote}{noPublic ? ` · ${t("Pas encore de prix public hors France pour cette gamme")}` : ""}{applyPromo ? ` · ${t("Promotion appliquée")} : ${activePromos.map((p) => p.name).join(", ")}` : ""}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-4">
          {visibleModels.length > 0 && (
            <div data-fit="models" className="overflow-x-auto rounded-xl border border-[#e8e0d2]">
              <table className={`w-full text-sm prices-table ${onePage ? "prices-options-table" : ""}`} data-testid="prices-table">
                <thead>{colHead(showPlaces)}</thead>
                <tbody>{sectionHead(t("Modèles"), colCount + (showPlaces ? 2 : 1))}</tbody>
                {groupByCat(visibleModels).map(([c, items]) => catBlock(c, items, "model"))}
              </table>
            </div>
          )}
          {visibleOptions.length > 0 && (
            <div data-fit="options" className={`overflow-x-auto rounded-xl border border-[#e8e0d2] ${visibleModels.length > 0 && !onePage ? "prices-print-break" : ""}`}>
              <table className="w-full text-sm prices-table prices-options-table" data-testid="prices-options-table">
                <thead>{colHead()}</thead>
                <tbody>{sectionHead(t("Options"))}</tbody>
                {groupByCat(visibleOptions).map(([c, items]) => catBlock(c, items, "option"))}
              </table>
            </div>
          )}
          {activePromos.length > 0 && (
            <div className="mt-3 text-xs text-[#5c4620] border-t border-[#e8e0d2] pt-2" data-fit="promo" data-testid="prices-promo-mention">
              <div className="font-bold text-[#0050b3]" data-testid="prices-promo-legend">{t("Prix en bleu et en gras : prix remisés par la promotion")}</div>
              {activePromos.map((p) => (
                <div key={p.id}>
                  <strong>{t("Promotion appliquée")} : {p.name}</strong>
                  {(p.start_date || p.end_date) && <> — {p.start_date && p.end_date ? `${t("du")} ${frDate(p.start_date)} ${t("au")} ${frDate(p.end_date)}` : p.end_date ? `${t("jusqu'au")} ${frDate(p.end_date)}` : `${t("à partir du")} ${frDate(p.start_date)}`}</>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
